import { useId, useState } from 'react'
import {
  FileTextOutlined,
  IconPunchOutTerminal,
  SafetyOutlined,
  TruckOutlined,
} from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { useToast } from '../../components/ui/ToastProvider'
import type { components } from '../../lib/api/schema.gen'
import { pluralize } from '../../lib/format/pluralize'
import { AddAddressModal, type NewAddressInput } from './AddAddressModal'
import { GenerateQuoteModal } from './GenerateQuoteModal'
import { formatMoney } from './cart-money'
import { describeCheckoutSplit, resolveCheckoutReadiness } from './cart-readiness'

type CartView = components['schemas']['CartView']

/** Sentinel `<option>` value for the Ship-to select's "＋ Add new address…"
 * entry — chosen so it can't collide with a real address id. */
const ADD_NEW_ADDRESS = '__add_new_address__'

export interface OrderSummaryRailProps {
  readonly cart: CartView
  readonly savingShipTo?: boolean
  readonly onShipToChange: (shipToAddressId: string) => void
  /** Persists a new ship-to address (`POST /account/addresses`) and refetches
   * the cart (`GET /cart`) so it appears in `cart.shipToAddresses`. It does NOT
   * `PUT /cart/ship-to` — the cart's ship-to id space is a separate seeded list
   * that rejects a freshly-created account-address id with 422 ("Unknown
   * ship-to address"); the rail reflects the new selection locally instead (see
   * `localSelectedId`), and it flows to an order via checkout. Owned by CartPage
   * (which holds the cart + apiClient). Returns the new address id so the rail
   * can show it selected. Optional (defaults to a no-op) only so presentational
   * tests that never open the add-address modal don't have to stub it. */
  readonly onCreateAddress?: (input: NewAddressInput) => Promise<string | undefined>
  readonly onProceedToCheckout: () => void
  /**
   * UID-403 (ADR-0125 D8) — open the Generate-quote modal immediately on mount,
   * for the RFQ page's "Generate quote" hand-off. `CartPage` owns reading AND
   * consuming that one-shot `location.state` intent (a history-entry payload
   * that must be stripped, ADR-0124 W3); this prop is only the resulting
   * initial modal state, so the rail stays unaware of the router.
   *
   * Deliberately an INITIAL value, not a controlled `open` prop: the modal's
   * open/closed state stays owned here, so consuming the intent (which
   * re-renders `CartPage` with the payload gone) cannot slam the modal shut
   * under the user — pinned by a test.
   */
  readonly autoOpenGenerateQuote?: boolean
  /** UID-431 — forwarded verbatim to `GenerateQuoteModal`'s own `onQuoteGenerated`
   * (see that prop's doc comment): fires once, on close, after a successful
   * generate-quote, so `CartPage` (which owns `cart`/`setCart`/`loadCart`) can
   * re-GET `/cart` and show the "Cart cleared — quote generated" toast. This rail
   * has no cart-mutation state of its own, so it only threads the callback through. */
  readonly onQuoteGenerated?: (() => void) | undefined
  /** True while ANY cart mutation is in flight elsewhere on the page (a line
   * quantity/config edit, a service removal, a ship-plan/delivery-option
   * change, or a ship-to save) — not just this rail's own state. Disables
   * "Continue to checkout" and "Generate quote" so neither action can fire
   * against a cart snapshot that's still mid-recalculation server-side. */
  readonly disabled?: boolean
}

/** No contract endpoint exists for a real PunchOut/ERP hand-off (Stage-1a ships only cart
 * line/ship-plan/ship-to mutations) — rather than a disabled stub, this is a deliberate DEMO
 * simulation (per the prototype's own `.fx-toast` pattern): the button is a real, enabled
 * action that fires a toast standing in for the real integration. */
const PUNCHOUT_TOAST_MESSAGE = 'Sent to your PunchOut / ERP'

/**
 * The Cart screen's sticky order-summary rail (Cart redesign): ship-to
 * address (drives sales tax) + the server-authoritative summary breakdown
 * (`CartView.summary`, ADR-0039) + primary/secondary CTAs. Every money
 * figure here is read directly off `summary` — this component derives
 * nothing itself (ADR-0029: totals are never client-recomputed once the
 * server derives them).
 */
export function OrderSummaryRail({
  cart,
  savingShipTo = false,
  onShipToChange,
  onCreateAddress = async () => undefined,
  onProceedToCheckout,
  autoOpenGenerateQuote = false,
  onQuoteGenerated,
  disabled = false,
}: OrderSummaryRailProps) {
  const shipToId = useId()
  const { summary } = cart
  const {
    canCheckout,
    reason: checkoutBlockedReason,
    readyLineCount,
    pendingLineCount,
  } = resolveCheckoutReadiness(cart)
  // UID-430 — the quote-generated modal's OWN "Proceed to checkout" reuses this
  // same readiness verdict, but a delivery option is a cart-level concern
  // unrelated to the quote just successfully generated inside that modal — see
  // `resolveCheckoutReadiness`'s doc comment.
  const { canCheckout: canProceedFromQuoteModal } = resolveCheckoutReadiness(cart, {
    requireDeliveryOption: false,
  })
  // UID-269 — checkout splits by readiness: a mixed cart is now expected and
  // checkout-able, so surface the split explicitly ("N ship now · M stay in
  // your cart") instead of silently proceeding. `undefined` for an all-ready
  // cart (nothing pending to call out).
  const readinessSplitNote = describeCheckoutSplit(readyLineCount, pendingLineCount)
  const [showGenerateQuote, setShowGenerateQuote] = useState(autoOpenGenerateQuote)
  const [showAddAddress, setShowAddAddress] = useState(false)
  // Reflect a just-created address as selected even if the cart couldn't
  // persist the selection (its ship-to id space can lag the account book by a
  // request). The address itself comes from the refetched `cart.shipToAddresses`
  // — this only overrides which one shows as selected.
  const [localSelectedId, setLocalSelectedId] = useState<string | null>(null)
  const { showToast } = useToast()

  const selectedAddressId = localSelectedId ?? summary.shipToAddressId
  const selectedAddress = cart.shipToAddresses.find((address) => address.id === selectedAddressId)
  // Default ship-to always lists first ("at top"). `Array.prototype.sort` is
  // stable (ES2019+), so every non-default address keeps its original order.
  // Which address is *selected* is still server-driven (`summary.shipToAddressId`)
  // — this only orders the options, never overrides the buyer's saved pick.
  const shipToOptions = [...cart.shipToAddresses].sort(
    (a, b) => Number(b.isDefault) - Number(a.isDefault),
  )

  function handleShipToChange(value: string) {
    if (value === ADD_NEW_ADDRESS) {
      setShowAddAddress(true)
      return
    }
    setLocalSelectedId(null)
    onShipToChange(value)
  }

  // Create via the caller (which persists + refetches the cart), then show the
  // new address selected. Any rejection propagates back to the modal (which
  // shows its own error and stays open), so it is intentionally NOT caught here.
  async function handleSaveAddress(input: NewAddressInput) {
    // Don't create a duplicate: if the same physical address is already in the
    // ship-to list (street/apt/city/state/ZIP, case-insensitive), select the
    // existing one instead of POSTing another copy.
    const norm = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase()
    const duplicate = cart.shipToAddresses.find(
      (address) =>
        norm(address.line1) === norm(input.line1) &&
        norm(address.line2 ?? '') === norm(input.line2) &&
        norm(address.city) === norm(input.city) &&
        norm(address.region) === norm(input.region) &&
        norm(address.postalCode) === norm(input.postalCode),
    )
    if (duplicate) {
      setLocalSelectedId(duplicate.id)
      setShowAddAddress(false)
      showToast('That address is already saved.')
      return
    }
    const newId = await onCreateAddress(input)
    if (newId) setLocalSelectedId(newId)
    setShowAddAddress(false)
    showToast('Address added')
  }

  return (
    <Card aria-labelledby="cart-summary-heading" className="cart-summary-rail">
      <h2 id="cart-summary-heading" className="heading-text">
        Order summary
      </h2>

      <div className="cart-summary-ship-to">
        <div className="cart-summary-ship-to-header">
          <label htmlFor={shipToId}>Ship to</label>
          <span className="caption-text">Sales tax {summary.salesTaxRatePct}%</span>
        </div>
        <select
          id={shipToId}
          value={selectedAddressId}
          disabled={savingShipTo}
          onChange={(event) => handleShipToChange(event.target.value)}
        >
          {shipToOptions.map((address) => (
            <option key={address.id} value={address.id}>
              {/* Design-fidelity fix: prototype option labels are
                  "{label} · {city}, {region}", not the bare label — helps
                  disambiguate two saved addresses with the same label.
                  `ShipToAddress` has no company/org field on this
                  contract (checked schema.gen.ts) — a company-suffixed
                  form isn't buildable, so this is the plain label·city,
                  state form. */}
              {address.label} · {address.city}, {address.region}
            </option>
          ))}
          {/* Opens the AddAddressModal (design's Ship-to "＋ Add new address…"
              entry). A sentinel value, not a real id — selecting it never
              sticks (the select is controlled by `selectedAddressId`). */}
          <option value={ADD_NEW_ADDRESS}>＋ Add new address…</option>
        </select>
        {selectedAddress && (
          // Prototype `:2178-2191` (design-fidelity fix): city/state/zip ONLY, not the
          // full street — the rail is a compact summary, not the address's canonical
          // display; the full street is still available wherever the address itself is
          // shown/edited (e.g. checkout). Deliberately hides line1/line2 here.
          <p className="caption-text cart-summary-ship-to-line">
            {selectedAddress.city}, {selectedAddress.region} {selectedAddress.postalCode}
          </p>
        )}
      </div>

      <dl className="cart-summary-totals">
        {/* UID-410 AC5: singular/plural agreement by count (was hardcoded "parts"). */}
        <dt>
          Part production ({summary.lineCount} {pluralize(summary.lineCount, 'part')})
        </dt>
        <dd>{formatMoney(summary.partsSubtotalAmount, summary.currency)}</dd>
        {Number(summary.corporateDiscountAmount) > 0 && (
          <>
            <dt className="cart-summary-discount-label">
              Corporate discount ({summary.corporateDiscountPct}%)
            </dt>
            <dd className="cart-summary-discount-value">
              -{formatMoney(summary.corporateDiscountAmount, summary.currency)}
            </dd>
          </>
        )}
        <dt>
          Shipping <span className="cart-summary-qualifier">({summary.shipmentLabel})</span>
        </dt>
        <dd>{formatMoney(summary.shippingAmount, summary.currency)}</dd>
        <dt>Tax</dt>
        <dd>{formatMoney(summary.taxAmount, summary.currency)}</dd>
        <dt className="cart-summary-total-label">Total</dt>
        <dd className="cart-summary-total-value">
          {formatMoney(summary.totalAmount, summary.currency)}
        </dd>
      </dl>
      {/* Screens.md "price-recalculating (flash)" state: a sighted user sees
          the total update; this POLITE live region announces the new total
          to assistive tech so the recalculation isn't a silent, sight-only
          event (WCAG F4). */}
      <p className="sr-only" aria-live="polite">
        Cart total updated: {formatMoney(summary.totalAmount, summary.currency)}
      </p>

      {/* UID-269 — checkout splits by readiness: a mixed cart is EXPECTED, so
          this note is informational (shown whenever checkout can proceed),
          never a block. */}
      {canCheckout && readinessSplitNote && (
        <p className="caption-text cart-readiness-split-note">{readinessSplitNote}</p>
      )}

      <Button
        variant="primary"
        size="lg"
        disabled={!canCheckout || disabled}
        onClick={onProceedToCheckout}
        className="cart-checkout-btn"
      >
        Continue to checkout
      </Button>
      {!canCheckout && checkoutBlockedReason && (
        <p className="caption-text">{checkoutBlockedReason}</p>
      )}

      {/* Deliberate demo simulation (no real PunchOut/ERP backend exists) — a real, enabled
          action that fires a toast standing in for the hand-off, per the prototype's own
          `.fx-toast` pattern (see PUNCHOUT_TOAST_MESSAGE's doc). */}
      <Button
        variant="secondary"
        size="lg"
        onClick={() => showToast(PUNCHOUT_TOAST_MESSAGE)}
        className="cart-summary-secondary-btn"
      >
        <IconPunchOutTerminal />
        Send to PunchOut
      </Button>
      <Button
        variant="secondary"
        size="lg"
        disabled={disabled}
        onClick={() => setShowGenerateQuote(true)}
        className="cart-summary-secondary-btn"
      >
        <FileTextOutlined aria-hidden="true" />
        Generate quote
      </Button>

      <div className="cart-summary-footnotes">
        {summary.arrivalEstimateLabel && (
          // arrivalEstimateLabel is already fully formed by the server,
          // e.g. "Arrives Tue, Aug 11 · One shipment" (cart-derivation.ts) — render
          // as-is; do NOT re-prefix "Arrives" or re-append the shipment label.
          <p className="caption-text cart-summary-footnote">
            <TruckOutlined aria-hidden="true" />
            {summary.arrivalEstimateLabel}
          </p>
        )}
        <p className="caption-text cart-summary-footnote">
          <SafetyOutlined aria-hidden="true" />
          Distributed across vetted partners to balance capacity.
        </p>
      </div>

      {/* UID-403 — the modal's OWN "Proceed to checkout" button used to be
          ungated while THIS rail's button (above) was gated on `canCheckout`,
          so the modal was a second, unguarded route to `/checkout`. A cart with
          no `ready` line reached checkout that way and was bounced straight back
          to `/cart` by CheckoutPage's empty-cart redirect — the ticket's
          "'Proceed to checkout' goes to an empty cart screen" bug. Both CTAs read
          `resolveCheckoutReadiness` — the modal's own CTA opts out of just the
          delivery-option gate (UID-430, see `canProceedFromQuoteModal` above). */}
      <GenerateQuoteModal
        cart={cart}
        open={showGenerateQuote}
        onClose={() => setShowGenerateQuote(false)}
        onProceedToCheckout={onProceedToCheckout}
        canProceedToCheckout={canProceedFromQuoteModal}
        proceedBlockedReason={checkoutBlockedReason}
        onQuoteGenerated={onQuoteGenerated}
      />

      <AddAddressModal
        open={showAddAddress}
        onClose={() => setShowAddAddress(false)}
        onSave={handleSaveAddress}
      />
    </Card>
  )
}
